// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DATA_FETCHING_STATUSES } from "lib/constants";
import axiosPrivate from "lib/config/axios";
import { API_SUCCESS_RESPONSE_TAG } from "lib/constants";
import { ENDPOINTS } from "lib/constants";
import { format, isValid, parseISO } from "date-fns";
import { REWARD_TYPES_MAP } from "lib/constants";
import { schema } from "./../../views/admin/rewards/schema";
import * as yup from "yup";

const rewardsArraySchema = yup.array().of(schema);

const formatRewardDateParam = (value) => {
  if (!value) return format(new Date(), "yyyy-MM-dd");

  if (typeof value === "string") {
    const parsedIsoDate = parseISO(value);
    if (isValid(parsedIsoDate)) {
      return format(parsedIsoDate, "yyyy-MM-dd");
    }

    const parsedNativeDate = new Date(value);
    if (isValid(parsedNativeDate)) {
      return format(parsedNativeDate, "yyyy-MM-dd");
    }

    return value;
  }

  if (isValid(value)) {
    return format(value, "yyyy-MM-dd");
  }

  return format(new Date(), "yyyy-MM-dd");
};

const REWARD_PAYLOAD_KEYS_MAP = {
  "Gift Card(Code)": "cardCode",
  "Card Type": "cardType",
  "Quantity/Amount": "rewardValue",
  "Reward Date": "rewardDate",
  "Reward Type": "rewardType",
  "Leaderboard Type": "leaderboardType",
  Rank: "rank",
};

export const transformRewards = (rewards) =>
  rewards.map((reward) => {
    const updatedReward = {};
    for (const key in reward) {
      const newKey = REWARD_PAYLOAD_KEYS_MAP[key] || key; // Use mapped key or default to original
      updatedReward[newKey] = reward[key];
    }

    if (updatedReward.rewardType !== REWARD_TYPES_MAP.GIFT_CARD) {
      const { cardCode, cardType, ...rest } = updatedReward;
      return rest;
    }
    return updatedReward;
  });

export const validateRewards = async (rewardsArray) => {
  try {
    const data = await rewardsArraySchema.validate(rewardsArray, {
      abortEarly: false,
    });
    return { hasErrors: false, error: null, data };
  } catch (error) {
    const formattedErrors = (error?.inner || [])
      .map((validationError) => {
        const rowMatch = validationError.path?.match?.(/\[(\d+)\]/);
        const rowNumber = rowMatch ? Number(rowMatch[1]) + 2 : null;
        return rowNumber
          ? `Row ${rowNumber}: ${validationError.message}`
          : validationError.message;
      })
      .filter(Boolean);

    return {
      hasErrors: true,
      error: formattedErrors[0] || error.message,
      data: null,
      errors: formattedErrors,
    };
  }
};

export const fetchRewardsThunk = createAsyncThunk(
  "appRewards/fetchRewards",
  async ({ page, selectedDate, sortBy }, { dispatch }) => {
    try {
      const response = await axiosPrivate.get(`${ENDPOINTS.REWARDS}/by-date`, {
        params: {
          page,
          rewardDate: formatRewardDateParam(selectedDate),
          // ...(sortBy ? { sortBy } : null),
          sortBy: "rank",
        },
      });
      const rewards = response.data;
      dispatch(
        setRewards({
          data: rewards,
          status: DATA_FETCHING_STATUSES.DATA_LOADED,
        })
      );
      return API_SUCCESS_RESPONSE_TAG;
    } catch (error) {
      if (error?.response?.data?.message?.toLowerCase() === "not found") {
        toast.error("Reward not found.");
        return;
      }
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
        return;
      }
      toast.error(error?.response?.data?.message ?? "Something went wrong");
    }
  }
);

export const bulkCreateRewardsThunk = createAsyncThunk(
  "appRewards/bulkCreateRewards",
  async (rewards, { dispatch }) => {
    const transformedRewards = transformRewards(rewards);
    const hasGiftCardReward = transformedRewards.some(
      (reward) => reward.rewardType === REWARD_TYPES_MAP.GIFT_CARD
    );

    if (hasGiftCardReward) {
      toast.error(
        "Bulk CSV upload does not support gift card rewards because each gift card requires an image."
      );
      return;
    }

    const { hasErrors, error, data, errors } = await validateRewards(
      transformedRewards
    );
    if (hasErrors) {
      toast.error(
        errors?.length
          ? `${error} (${errors.length} validation issue${errors.length > 1 ? "s" : ""})`
          : error ?? "CSV file has invalid data."
      );
      return;
    }

    try {
      await axiosPrivate.post(`${ENDPOINTS.REWARDS}/bulk-rewards`, {
        rewards: data,
      });
      await dispatch(
        fetchRewardsThunk({
          page: 1,
          selectedDate: new Date(),
        })
      );
      toast.success("Rewards successfully created");
      return API_SUCCESS_RESPONSE_TAG;
    } catch (error) {
      if (error?.response?.data?.message?.toLowerCase() === "not found") {
        toast.error("Reward not found.");
      } else {
        toast.error(error?.response?.data?.message ?? "Something went wrong");
      }
    }
  }
);

export const createRewardsThunk = createAsyncThunk(
  "appRewards/fetchRewards",
  async (payload, { dispatch }) => {
    try {
      let formData = new FormData();
      payload.cardCode && formData.append("cardCode", payload.cardCode);
      payload.cardType && formData.append("cardType", payload.cardType);
      formData.append("rank", payload.rank);
      formData.append("rewardType", payload.rewardType);
      formData.append("rewardDate", payload.rewardDate);
      formData.append("rewardValue", payload.rewardValue);
      formData.append("leaderboardType", payload.leaderboardType);

      const cardImageFile = payload.cardImage?.[0] ?? payload.cardImage;
      if (cardImageFile) {
        formData.append("cardImage", cardImageFile, cardImageFile.name);
      }

      await axiosPrivate.post(ENDPOINTS.REWARDS, formData, {
        headers: {
          "Content-Type": "multipart/form-data; boundary=myboundary",
        },
      });
      await dispatch(
        fetchRewardsThunk({
          page: 1,
          selectedDate: payload.rewardDate,
        })
      );
      toast.success("Reward successfully created");
      return API_SUCCESS_RESPONSE_TAG;
    } catch (error) {
      if (error?.response?.data?.message?.toLowerCase() === "not found") {
        toast.error("Reward not found.");
      } else {
        toast.error(error?.response?.data?.message ?? "Something went wrong");
      }
    }
  }
);

export const updateRewardsThunk = createAsyncThunk(
  "appRewards/updateRewards",
  async (payload, { dispatch }) => {
    try {
      const { id, selectedRewardDate, cardImage, ...restPayload } = payload;
      const cardImageFile = cardImage?.[0] ?? cardImage;
      if (cardImageFile) {
        const formData = new FormData();
        Object.entries(restPayload).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value);
          }
        });
        formData.append("cardImage", cardImageFile, cardImageFile.name);
        await axiosPrivate.patch(`${ENDPOINTS.REWARDS}/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data; boundary=myboundary",
          },
        });
      } else {
        await axiosPrivate.patch(`${ENDPOINTS.REWARDS}/${id}`, restPayload);
      }
      await dispatch(
        fetchRewardsThunk({
          page: 1,
          selectedDate: selectedRewardDate,
        })
      );
      toast.success("Reward successfully updated.");
      return API_SUCCESS_RESPONSE_TAG;
    } catch (error) {
      if (error?.response?.data?.message?.toLowerCase() === "not found") {
        toast.error("Reward not found.");
        return;
      }
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
        return;
      }
      toast.error(error?.response?.data?.message ?? "Something went wrong");
    }
  }
);

export const deleteRewardsThunk = createAsyncThunk(
  "appRewards/deleteRewards",
  async ({ id, selectedDate }, { dispatch }) => {
    try {
      await axiosPrivate.delete(`${ENDPOINTS.REWARDS}/${id}`);
      await dispatch(
        fetchRewardsThunk({
          page: 1,
          selectedDate,
        })
      );
      toast.success("Reward successfully deleted.");
      return API_SUCCESS_RESPONSE_TAG;
    } catch (error) {
      if (error?.response?.data?.message?.toLowerCase() === "not found") {
        toast.error("Reward not found.");
        return;
      }
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
        return;
      }
      toast.error(error?.response?.data?.message ?? "Something went wrong");
    }
  }
);

const initialState = {
  rewards: {
    status: DATA_FETCHING_STATUSES.DATA_LOADING,
    data: [],
  },
};

export const rewardsSlice = createSlice({
  name: "rewardsSlice",
  initialState,
  reducers: {
    setRewards: (state, action) => {
      state.rewards = {
        ...state.rewards,
        ...action.payload,
      };
    },
  },
});
export const { setRewards } = rewardsSlice.actions;

export default rewardsSlice.reducer;

//Selector
export const useRewardsSelector = () =>
  useSelector((state) => {
    return state.rewardsReducer;
  });
